import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import DeviceManagement from './components/DeviceManagement';
import Reports from './components/Reports';
import Sidebar from './components/Sidebar';
import Venues from './components/Venues';
import VenueDetails from './components/VenueDetails';
import AddDevice from './components/AddDevice';
import ComprehensiveMonitoring from './components/ComprehensiveMonitoring';
import UserManagement from './components/UserManagement';
import Settings from './components/Settings';
import Profile from './components/Profile';
import RecordingDashboard from './components/recordings/RecordingDashboard';
import RecordingDetail from './components/recordings/RecordingDetail';
import GlobalAudioPlayer from './components/audio/GlobalAudioPlayer';
import { RecordingProvider } from './contexts/RecordingContext';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { collapsed } = useSidebar();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-lg text-gray-900 dark:text-gray-100">Loading...</div>
      </div>
    );
  }

  return user ? (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      {/* Main content area with dynamic margin for sidebar and bottom padding for player */}
      <div
        className={`pt-16 lg:pt-0 pb-24 transition-all duration-300 ${
          collapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}
      >
        <div className="min-h-screen">
          {children}
        </div>
      </div>
      {/* Global audio player */}
      <GlobalAudioPlayer />
    </div>
  ) : (
    <Navigate to="/login" />
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <NotificationProvider>
          <AuthProvider>
            <SidebarProvider>
              <AudioPlayerProvider>
                <RecordingProvider>
                  <Router>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/venues"
                      element={
                        <ProtectedRoute>
                          <Venues />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/venues/:venueId"
                      element={
                        <ProtectedRoute>
                          <VenueDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/venues/:venueId/add-device"
                      element={
                        <ProtectedRoute>
                          <AddDevice />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/devices"
                      element={
                        <ProtectedRoute>
                          <DeviceManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/monitoring"
                      element={
                        <ProtectedRoute>
                          <ComprehensiveMonitoring />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/monitoring/:venueId"
                      element={
                        <ProtectedRoute>
                          <ComprehensiveMonitoring />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <ProtectedRoute>
                          <Reports />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/recordings"
                      element={
                        <ProtectedRoute>
                          <RecordingDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/recordings/:id"
                      element={
                        <ProtectedRoute>
                          <RecordingDetail />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Router>
                </RecordingProvider>
              </AudioPlayerProvider>
            </SidebarProvider>
          </AuthProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;